import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { BrandLogo } from "@/components/shared/BrandLogo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { SidebarProvider } from "@/contexts/SidebarContext";

interface PublicWizardLayoutProps {
  children: ReactNode;
}

/**
 * Minimal chrome for the consumer wizard at /requests/new when reached anonymously
 * (guest checkout). Renders only a top bar with the brand mark and a language switcher.
 * Deliberately omits the app sidebar and any hooks that assume an authenticated user
 * so it can mount without a session. Sign-in is intentionally NOT exposed here —
 * returning users sign in via the AuthModal's "Ya tengo cuenta" tab at the payment
 * step, which preserves wizard state across the auth round-trip. Linking to /login
 * from the header would discard whatever the visitor has filled in.
 *
 * The SidebarProvider is included only because the wizard page calls `useSidebar()`
 * for its bottom-fixed footer layout — we never render an actual sidebar here,
 * which is exactly what `hasSidebar={false}` tells it. The wizard's fixed footer
 * reserves 16rem on the left for a sidebar, and it used to decide that from auth
 * state; since lazy registration lets a visitor authenticate WITHOUT leaving this
 * shell (RequestsNewWizardRoute locks the shell at first render), the footer
 * started offsetting for a sidebar that is not here.
 */
export function PublicWizardLayout({ children }: PublicWizardLayoutProps): JSX.Element {
  return (
    <SidebarProvider hasSidebar={false}>
      <div className="flex h-[100dvh] flex-col bg-muted/40 dark:bg-muted">
        <header className="flex h-14 shrink-0 items-center justify-between border-b bg-background px-4 md:h-16 md:px-6">
          <Link to="/" aria-label="Gestionalo" className="flex items-center">
            <BrandLogo variant="full" imgClassName="h-7 md:h-8" />
          </Link>
          {/*
            Theme before language, matching Header's order so the two shells do
            not disagree about where a control lives.

            This shell had neither until the wizard became something an
            anonymous visitor SPENDS TIME IN. While it was a short guest
            checkout, chrome was reasonably trimmed to the minimum; a multi-turn
            conversation is long enough that someone who prefers dark should not
            have to sign in to get it. PortalThemeProvider wraps the whole
            router, so the preference is the same one the app uses and survives
            the trip into AppLayout after registering.
          */}
          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </SidebarProvider>
  );
}
